import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProjectCard } from '@/components/projects/ProjectCard';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Plus, Grid, List } from 'lucide-react';
import { mockProjects } from '@/lib/mock-entities';
import type { ProjectStatus } from '@/types/entities';

export default function ProjectsList() {
  const navigate = useNavigate();
  const [view, setView] = useState<'grid' | 'kanban'>('grid');
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | 'all'>('all');

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 500));
      return mockProjects;
    },
  });

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (statusFilter === 'all') return projects;
    return projects.filter((p) => p.status === statusFilter);
  }, [projects, statusFilter]);

  const projectsByStatus = useMemo(() => {
    if (!projects) return {};
    return {
      planning: projects.filter((p) => p.status === 'planning'),
      'in-progress': projects.filter((p) => p.status === 'in-progress'),
      review: projects.filter((p) => p.status === 'review'),
      completed: projects.filter((p) => p.status === 'completed'),
      'on-hold': projects.filter((p) => p.status === 'on-hold'),
    };
  }, [projects]);

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Projects" />
        <main className="flex-1 overflow-auto p-6 flex items-center justify-center">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="Projects">
        <div className="flex gap-2">
          <Button
            variant={view === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'kanban' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('kanban')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button onClick={() => navigate('/projects/new')}>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </Header>

      <main className="flex-1 overflow-auto p-6">
        {view === 'grid' ? (
          <div>
            <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as ProjectStatus | 'all')}>
              <TabsList className="mb-6">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="planning">Planning</TabsTrigger>
                <TabsTrigger value="in-progress">In Progress</TabsTrigger>
                <TabsTrigger value="review">Review</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
                <TabsTrigger value="on-hold">On Hold</TabsTrigger>
              </TabsList>

              <TabsContent value={statusFilter}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.map((project) => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </div>
                {filteredProjects.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No projects found in this category
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {Object.entries(projectsByStatus).map(([status, statusProjects]: [string, typeof projects]) => (
              <div key={status} className="flex-shrink-0 w-80">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold capitalize">
                      {status.replace('-', ' ')}
                    </h3>
                    <span className="text-sm text-gray-600">
                      {statusProjects?.length || 0}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {statusProjects?.map((project) => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
